import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, apiClient, API } from '../App';
import { MessageCircle, Send, X, Users, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { format } from 'date-fns';

export default function ChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  // WebSocket connection
  useEffect(() => {
    if (!user?.id) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '').replace('/api', '') || 'localhost:8001';
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${user.id}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Chat WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          // Update messages if in the same chat
          if (selectedChat?.id === data.chat_id) {
            setMessages(prev => [...prev, {
              id: data.message.id,
              chat_id: data.chat_id,
              sender_id: data.message.sender_id,
              sender_name: data.message.sender_name,
              text: data.message.text,
              is_read: data.message.sender_id === user.id,
              created_at: data.message.created_at
            }]);
          }
          
          // Update chat list
          loadChats();
        }
      };
      
      ws.onclose = () => {
        console.log('Chat WebSocket disconnected');
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      wsRef.current = ws;
      
      return () => {
        ws.close();
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [user?.id, selectedChat?.id]);

  // Load chats
  const loadChats = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiClient.get('/chat/list');
      setChats(res.data);
      const total = res.data.reduce((sum, chat) => sum + (chat.unread_count || 0), 0);
      setUnreadTotal(total);
    } catch (error) {
      console.error('Failed to load chats');
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      loadChats();
    }
  }, [isOpen, loadChats]);

  // Load messages for selected chat
  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.id);
    }
  }, [selectedChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async (chatId) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/chat/${chatId}/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    try {
      // Send via REST API
      await apiClient.post(`/chat/${selectedChat.id}/send`, {
        chat_id: selectedChat.id,
        text: newMessage
      });
      
      // Add message locally for immediate feedback
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        chat_id: selectedChat.id,
        sender_id: user.id,
        sender_name: user.company_name || `${user.first_name} ${user.last_name}`,
        text: newMessage,
        is_read: false,
        created_at: new Date().toISOString()
      }]);
      
      setNewMessage('');
      loadChats();
    } catch (error) {
      console.error('Failed to send message');
    }
  };

  const getOtherParticipantName = (chat) => {
    if (!chat?.participant_names) return 'Unknown';
    const otherId = chat.participants.find(p => p !== user?.id);
    return chat.participant_names[otherId] || 'Unknown';
  };

  if (!user) return null;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-105"
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="h-6 w-6" />
        {unreadTotal > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadTotal > 9 ? '9+' : unreadTotal}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
            {selectedChat ? (
              <>
                <button onClick={() => setSelectedChat(null)} className="p-1 hover:bg-slate-800 rounded">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h3 className="font-semibold flex-1 mx-2 truncate">
                  {getOtherParticipantName(selectedChat)}
                </h3>
              </>
            ) : (
              <>
                <h3 className="font-semibold flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Messages
                </h3>
              </>
            )}
            <button onClick={() => { setIsOpen(false); setSelectedChat(null); }} className="p-1 hover:bg-slate-800 rounded" data-testid="close-chat-btn">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          {selectedChat ? (
            // Chat Messages View
            <div className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 p-4">
                {loading ? (
                  <div className="flex justify-center py-4">
                    <div className="spinner w-6 h-6"></div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <MessageCircle className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No messages yet</p>
                    <p className="text-sm">Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-xl px-4 py-2 ${
                            msg.sender_id === user.id
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          <p className="text-sm">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-orange-100' : 'text-slate-500'}`}>
                            {format(new Date(msg.created_at), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="chat-message-input"
                />
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={!newMessage.trim()} data-testid="send-message-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          ) : (
            // Chat List View
            <ScrollArea className="flex-1">
              {chats.length === 0 ? (
                <div className="text-center text-slate-500 py-8 px-4">
                  <Users className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p>No conversations yet</p>
                  <p className="text-sm">Start chatting with carriers or customers from their profile</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => setSelectedChat(chat)}
                      className="w-full p-4 text-left hover:bg-slate-50 transition-colors flex items-center gap-3"
                      data-testid={`chat-item-${chat.id}`}
                    >
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-900 truncate">
                            {getOtherParticipantName(chat)}
                          </span>
                          {chat.unread_count > 0 && (
                            <span className="w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {chat.last_message || 'No messages'}
                        </p>
                        {chat.last_message_time && (
                          <p className="text-xs text-slate-400">
                            {format(new Date(chat.last_message_time), 'MMM d, h:mm a')}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      )}
    </>
  );
}
